/**
 * FormRepeatableRenderer — renders the form layout N times in batch-create mode.
 * Delegates per-row rendering (with scoped effects/observers) to FormRepeatableRow.
 */

import React from 'react';
import { Stack } from '@packages/ui/layout';
import { Button } from '@packages/ui/primitives/Button';
import { FormRepeatableRow } from './FormRepeatableRow';
import type { FormLayoutNode } from '../types/layout';
import type { FormEffect, FormObserver } from '../types/block';
import type { RepeatableProcessorReturn } from '../processors/repeatableProcessor';
import type { FormPrecisionConfig } from '../precision/precisionContract';

interface FormRepeatableRendererProps {
  repeatable: RepeatableProcessorReturn;
  layout: FormLayoutNode[];
  effects?: FormEffect[];
  observers?: FormObserver[];
  precisionConfig?: FormPrecisionConfig;
}

export const FormRepeatableRenderer = React.memo(function FormRepeatableRenderer({
  repeatable,
  layout,
  effects = [],
  observers = [],
  precisionConfig,
}: FormRepeatableRendererProps): React.ReactElement {
  const { fields, append, remove, canAdd, canRemove, addLabel, addButton, cardConfig } = repeatable;

  const rawLabel = addLabel.replace(/^Add\s+/i, '').replace(/^Another\s+/i, '');
  const rowLabel = rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1);

  const buttonVariant = addButton.variant ?? 'primary-dark';
  const buttonIcon = addButton.icon;
  const buttonFullWidth = addButton.fullWidth ?? true;

  return (
    <Stack gap='16px'>
      {fields.map((field, index) => (
        <FormRepeatableRow
          key={field.id}
          index={index}
          layout={layout}
          effects={effects}
          observers={observers}
          precisionConfig={precisionConfig}
          canRemove={canRemove}
          onRemove={remove}
          rowLabel={rowLabel}
          cardConfig={cardConfig}
        />
      ))}
      {canAdd && (
        <Button
          variant={buttonVariant}
          size='md'
          semanticId='addRepeatableRow'
          icon={buttonIcon}
          onClick={append}
          style={buttonFullWidth ? { width: '100%' } : undefined}
        >
          {addLabel}
        </Button>
      )}
    </Stack>
  );
});
